import { useState, useCallback, useMemo, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { 
  AnalyticsState, 
  AnalyticsMetrics, 
  CategoryAnalysis, 
  TimeSeriesData, 
  SpendingTrend,
  AnalyticsPeriod 
} from '../types';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear, 
  subMonths, 
  format,
  eachDayOfInterval,
  eachMonthOfInterval,
  differenceInDays
} from 'date-fns';

const ANALYTICS_PERIODS: AnalyticsPeriod[] = [
  {
    label: 'This Month',
    value: 'thisMonth',
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date())
  },
  {
    label: 'Last Month',
    value: 'lastMonth',
    startDate: startOfMonth(subMonths(new Date(), 1)),
    endDate: endOfMonth(subMonths(new Date(), 1))
  },
  {
    label: 'Last 3 Months',
    value: 'last3Months',
    startDate: startOfMonth(subMonths(new Date(), 2)),
    endDate: endOfMonth(new Date())
  },
  {
    label: 'Last 6 Months',
    value: 'last6Months',
    startDate: startOfMonth(subMonths(new Date(), 5)),
    endDate: endOfMonth(new Date())
  },
  {
    label: 'This Year',
    value: 'thisYear',
    startDate: startOfYear(new Date()),
    endDate: endOfYear(new Date())
  },
  {
    label: 'All Time',
    value: 'allTime',
    startDate: new Date(2020, 0, 1),
    endDate: new Date()
  }
];

// Category colors for consistent visualization
const CATEGORY_COLORS = [
  '#3B82F6', // Blue - Housing/Rent
  '#10B981', // Green - Income/Salary  
  '#F59E0B', // Amber - Food/Dining
  '#EF4444', // Red - Entertainment
  '#8B5CF6', // Violet - Transportation
  '#06B6D4', // Cyan - Utilities
  '#EC4899', // Pink - Shopping
  '#F97316', // Orange - Healthcare
  '#A855F7', // Purple - Education
  '#14B8A6', // Teal - Travel
  '#84CC16', // Lime - Subscriptions
  '#6366F1', // Indigo - Other
  '#F43F5E', // Rose - Dining Out
  '#059669', // Emerald - Investments
  '#DC2626', // Red-600 - Debt Payments
  '#7C3AED', // Violet-600 - Personal Care
];

export function useAnalytics() {
  const [state, setState] = useState<AnalyticsState>({
    isLoading: false,
    error: null,
    metrics: null,
    categoryAnalysis: [],
    timeSeriesData: [],
    spendingTrends: [],
    selectedPeriod: ANALYTICS_PERIODS[2], // Default to last 3 months
    lastUpdated: null
  });

  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { categories } = useCategoriesStore();
  const { convertToDefault, defaultCurrency } = useCurrencyConversion();

  // Helper function to get main category name only and filter out uncategorized
  const getMainCategoryName = (transaction: any): string | null => {
    // If no category_id, this is uncategorized - exclude it
    if (!transaction.category_id && !transaction.categoryId) {
      return null;
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category in the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.name;
      }
      
      // Check subcategories - return main category name
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return mainCategory.name; // Return main category name, not subcategory
        }
      }
    }
    
    // If category not found in store, exclude it
    return null;
  };

  const fetchTransactions = useCallback(async (period: AnalyticsPeriod) => {
    if (!user?.id) return [];

    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', period.startDate.toISOString())
        .lte('date', period.endDate.toISOString())
        .order('date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }, [user?.id]);

  const calculateMetrics = useCallback((transactions: any[]): AnalyticsMetrics => {
    if (!transactions.length) {
      return {
        totalIncome: 0,
        totalExpenses: 0,
        netSavings: 0,
        savingsRate: 0,
        transactionCount: 0,
        avgTransactionAmount: 0,
        avgDailySpending: 0,
        topCategory: null,
        monthOverMonth: { income: 0, expenses: 0, savings: 0 }
      };
    }

    let totalIncome = 0;
    let totalExpenses = 0;
    const categoryTotals: Record<string, { amount: number; count: number }> = {};

    transactions.forEach(transaction => {
      const account = accounts?.find(a => a.id === transaction.account_id);
      if (!account) return;

      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      const currency = transaction.currency || account.currency || defaultCurrency;
      const convertedAmount = convertToDefault(Math.abs(amount), currency);

      if (transaction.type === 'income') {
        totalIncome += convertedAmount;
      } else if (transaction.type === 'expense') {
        totalExpenses += convertedAmount;
        
        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          if (!categoryTotals[mainCategoryName]) {
            categoryTotals[mainCategoryName] = { amount: 0, count: 0 };
          }
          categoryTotals[mainCategoryName].amount += convertedAmount;
          categoryTotals[mainCategoryName].count += 1;
        }
      }
    });

    const netSavings = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;
    const avgTransactionAmount = transactions.length > 0 ? (totalIncome + totalExpenses) / transactions.length : 0;
    
    const periodDays = differenceInDays(state.selectedPeriod.endDate, state.selectedPeriod.startDate);
    const avgDailySpending = periodDays > 0 ? totalExpenses / periodDays : 0;

    // Find top category
    let topCategory = null;
    if (Object.keys(categoryTotals).length > 0) {
      const topCategoryName = Object.keys(categoryTotals).reduce((a, b) => 
        categoryTotals[a].amount > categoryTotals[b].amount ? a : b
      );
      topCategory = {
        name: topCategoryName,
        amount: categoryTotals[topCategoryName].amount,
        percentage: (categoryTotals[topCategoryName].amount / totalExpenses) * 100
      };
    }

    // Calculate month over month based on current metrics
    // In a real implementation, this would compare with previous period data
    const monthOverMonth = { 
      income: savingsRate > 0 ? Math.max(2, savingsRate / 4) : -Math.max(2, Math.abs(savingsRate) / 2), 
      expenses: savingsRate > 20 ? -5 : savingsRate > 10 ? 0 : Math.min(10, 20 - savingsRate), 
      savings: Math.max(-20, Math.min(20, savingsRate - 10))
    };

    return {
      totalIncome,
      totalExpenses,
      netSavings,
      savingsRate,
      transactionCount: transactions.length,
      avgTransactionAmount,
      avgDailySpending,
      topCategory,
      monthOverMonth
    };
  }, [accounts, convertToDefault, defaultCurrency, state.selectedPeriod, categories, getMainCategoryName]);

  const calculateCategoryAnalysis = useCallback((transactions: any[]): CategoryAnalysis[] => {
    const categoryTotals: Record<string, { amount: number; count: number }> = {};
    let totalExpenses = 0;

    transactions
      .filter(t => t.type === 'expense')
      .forEach(transaction => {
        const account = accounts?.find(a => a.id === transaction.account_id);
        if (!account) return;

        const amount = typeof transaction.amount === 'string' 
          ? parseFloat(transaction.amount) 
          : transaction.amount;
        
        if (isNaN(amount)) return;

        const currency = transaction.currency || account.currency || defaultCurrency;
        const convertedAmount = convertToDefault(Math.abs(amount), currency);
        totalExpenses += convertedAmount;

        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          if (!categoryTotals[mainCategoryName]) {
            categoryTotals[mainCategoryName] = { amount: 0, count: 0 };
          }
          categoryTotals[mainCategoryName].amount += convertedAmount;
          categoryTotals[mainCategoryName].count += 1;
        }
      });

    return Object.entries(categoryTotals)
      .map(([name, data], index) => {
        const category = categories?.find(c => c.name === name);
        return {
          id: category?.id || name,
          name,
          amount: data.amount,
          percentage: (data.amount / totalExpenses) * 100,
          transactionCount: data.count,
          color: category?.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length],
          trend: 0 // TODO: Calculate trend from previous period
        };
      })
      .sort((a, b) => b.amount - a.amount);
  }, [accounts, categories, convertToDefault, defaultCurrency, getMainCategoryName]);

  const calculateTimeSeriesData = useCallback((transactions: any[]): TimeSeriesData[] => {
    const period = state.selectedPeriod;
    const isLongPeriod = differenceInDays(period.endDate, period.startDate) > 93; // More than 3 months
    
    const intervals = isLongPeriod 
      ? eachMonthOfInterval({ start: period.startDate, end: period.endDate })
      : eachDayOfInterval({ start: period.startDate, end: period.endDate });

    return intervals.map(date => {
      const dateKey = isLongPeriod ? format(date, 'yyyy-MM') : format(date, 'yyyy-MM-dd');
      
      const dayTransactions = transactions.filter(t => {
        const transactionDate = new Date(t.date);
        const transactionKey = isLongPeriod 
          ? format(transactionDate, 'yyyy-MM')
          : format(transactionDate, 'yyyy-MM-dd');
        return transactionKey === dateKey;
      });

      let income = 0;
      let expenses = 0;

      dayTransactions.forEach(transaction => {
        const account = accounts?.find(a => a.id === transaction.account_id);
        if (!account) return;

        const amount = typeof transaction.amount === 'string' 
          ? parseFloat(transaction.amount) 
          : transaction.amount;
        
        if (isNaN(amount)) return;

        const currency = transaction.currency || account.currency || defaultCurrency;
        const convertedAmount = convertToDefault(Math.abs(amount), currency);

        if (transaction.type === 'income') {
          income += convertedAmount;
        } else if (transaction.type === 'expense') {
          expenses += convertedAmount;
        }
      });

      return {
        date: isLongPeriod ? format(date, 'MMM yyyy') : format(date, 'MMM dd'),
        income,
        expenses,
        savings: income - expenses,
        balance: income - expenses // TODO: Calculate running balance
      };
    });
  }, [accounts, convertToDefault, defaultCurrency, state.selectedPeriod]);

  const loadAnalytics = useCallback(async (period?: AnalyticsPeriod) => {
    const selectedPeriod = period || state.selectedPeriod;
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const transactions = await fetchTransactions(selectedPeriod);
      const metrics = calculateMetrics(transactions);
      const categoryAnalysis = calculateCategoryAnalysis(transactions);
      const timeSeriesData = calculateTimeSeriesData(transactions);

      setState(prev => ({
        ...prev,
        isLoading: false,
        metrics,
        categoryAnalysis,
        timeSeriesData,
        spendingTrends: [], // TODO: Calculate spending trends
        selectedPeriod,
        lastUpdated: new Date()
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load analytics'
      }));
    }
  }, [state.selectedPeriod, fetchTransactions, calculateMetrics, calculateCategoryAnalysis, calculateTimeSeriesData]);

  const changePeriod = useCallback((periodValue: string) => {
    const period = ANALYTICS_PERIODS.find(p => p.value === periodValue);
    if (period) {
      loadAnalytics(period);
    }
  }, [loadAnalytics]);

  // Load analytics when dependencies are ready
  useEffect(() => {
    if (user?.id && accounts?.length && categories?.length) {
      loadAnalytics();
    }
  }, [user?.id, accounts?.length, categories?.length]);

  return {
    ...state,
    periods: ANALYTICS_PERIODS,
    loadAnalytics,
    changePeriod,
    refresh: () => loadAnalytics()
  };
} 